import qs from 'querystring'
import { Box, Heading, IconButton, Spacer, Stack, Text } from '@chakra-ui/react'
import { faFlag } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRouter } from 'next/router'
import React from 'react'

import {
  SESSION_FEEDBACK_MAX_DAYS_BACK,
  SESSION_FEEDBACK_NUM_MINUTES_EARLY,
} from '@/utils/constants'

import { SESSION_FEEDBACK_TYPE_DIALOGUES } from '@/queries/constants'
import DetectedIssueQ from '@/queries/DetectedIssueQ'
import NotificationQ from '@/queries/NotificationQ'
import PlatformFeedbackQ from '@/queries/PlatformFeedbackQ'
import SeriesHostQ from '@/queries/SeriesHostQ'
import SeriesQ from '@/queries/SeriesQ'
import SessionQ from '@/queries/SessionQ'
import UserAdminMessageQ from '@/queries/UserAdminMessageQ'
import UserQ from '@/queries/UserQ'
import WorldQ from '@/queries/WorldQ'
import { useAppContext } from '@/client/context/app'
import { useQuery } from '@/client/context/query'
import { useUserRequired } from '@/client/context/user'
import useOneTimeAcknowledgement from '@/client/hooks/useOneTimeAcknowledgement'
import { SQL } from '@/client/query'
import isDetectedIssueDisputable from '@/client/services/moderation/isDetectedIssueDisputable'
import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import { SafetyReportStatus } from '@/client/services/safetyReport'
import { SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS } from '@/client/services/satBootcamp/constants'
import { getSessionUrlById } from '@/client/services/url'
import { isUserOnboarding, isUserSuspended } from '@/client/services/user'
import { PROGRAM_SUBWORLD_SLUGS, SAT_BOOTCAMP_WORLD_SLUG } from '@/client/services/worlds/constants'
import getNotificationKey from '@/client/services/worlds/notifications/getNotificationKey'

import MissingDateOfBirthModal from '@/components/GlobalModal/MissingDateOfBirthModal'
import SessionSummary from '@/components/Session/SessionSummary/SessionSummary'
import SessionFeedbackForm from '@/components/SessionFeedback/SessionFeedbackForm'
import ShwLink from '@/components/ShwLink'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import Tippy from '@/components/UI/Tippy'

import PublishBootcampOnLevelUpModal from './PublishBootcampOnLevelUpModal'
import ReviewRemovedLearnerModal from './ReviewRemovedLearnerModal'
import SeriesFeedbackModalTutor from './SeriesFeedbackModalTutor'
import UserAdminMessageModal from './UserAdminMessageModal'

export default function GlobalModal(): JSX.Element | null {
  const query = useQuery()
  const user = useUserRequired()
  const router = useRouter()
  const renderContext = useAppContext()
  const [open, setOpen] = React.useState(true)

  const me = query.one(UserQ.where({ id: user.id }))
  const session = me.one(UserQ.getFirstSessionThatRequiresFeedbackOrAudit())
  const dobIsSet = me<boolean>`${UserQ.date_of_birth} IS NOT NULL` ?? false

  const userAdminMessage = query.one(
    UserAdminMessageQ.where({
      recipientUserId: user.id,
      acknowledged: false,
      include: [{ association: 'User', required: false }],
      order: [[UserAdminMessageQ.created_at, 'asc']],
      limit: 1,
    })
  )
  const userAdminMessageUserId = userAdminMessage(UserQ.id)
  const userAdminMessageUserAvatarUrl = userAdminMessage(UserQ.avatar_url)
  const userAdminMessageUserDisplayName = userAdminMessage(UserQ.display_name)

  const userAdminMessageId = userAdminMessage(UserAdminMessageQ.id)
  const userAdminMessageContent =
    userAdminMessage(UserAdminMessageQ.content) ?? EMPTY_RICH_TEXT_VALUE
  const userAdminMessageAcknowledgmentText = userAdminMessage(UserAdminMessageQ.acknowledgment_text)
  const userAdminMessageType = userAdminMessage(UserAdminMessageQ.type) ?? 'manual'

  const detectedIssue = userAdminMessage.one(
    DetectedIssueQ.where({ id: UserAdminMessageQ.detected_issue_id })
  )
  const detectedIssueType = detectedIssue(DetectedIssueQ.type)
  const isDisputable =
    detectedIssueType != null ? isDetectedIssueDisputable(detectedIssueType) : true

  const sessionId = session(SessionQ.id)

  const count = me(UserQ.countSessionsThatRequireFeedbackOrAudit()) ?? 0

  const tutor = session.one(SessionQ.getTutor())

  const feedbackType = session(SessionQ.getSessionFeedbackType())
  const isDialoguesFeedback = feedbackType === SESSION_FEEDBACK_TYPE_DIALOGUES

  // This is a hack to avoid having to fetch the SAT Bootcamp world id from query builder,
  // which causes a conditional query error.
  const maybeSATBootcampWorld = Object.entries(user.worlds).find(
    ([_, { slug }]) => slug === SAT_BOOTCAMP_WORLD_SLUG
  )

  const satBootcampTutorLeveledUpNotification = query.one(
    NotificationQ.where({
      userId: user.id,
      key: getNotificationKey({
        type: 'user_level_up',
        args: {
          worldId: Number.parseInt(maybeSATBootcampWorld?.[0] ?? '0', 10),
          level: 'tutor',
        },
      }),
    })
  )

  const satBootcampTutorLeveledUpNotifyAt = satBootcampTutorLeveledUpNotification(
    NotificationQ.notify_at
  )

  // 10 minutes before ending of second last session in series that belongs to a program subworld
  // and has not been given NPS feedback yet
  const seriesForTutorNPS = query.one(
    SeriesQ.where(
      {
        userId: user.id,
        limit: 1,
        include: [
          {
            association: 'World',
            required: true,
            where: SQL`${WorldQ.slug} = ANY(${[...PROGRAM_SUBWORLD_SLUGS]})`,
          },
        ],
      },
      SQL`NOT EXISTS(SELECT 1 ${SeriesHostQ.where(
        { userId: user.id, seriesId: SeriesQ.id },
        SQL`${SeriesHostQ.feedback_nps} IS NOT NULL`
      )})
      AND EXISTS(SELECT 1 ${UserQ.where({ id: user.id }, SQL`${UserQ.hasTutorRole()}`)})
      AND (
       SELECT ${SessionQ.getComputedEndDate()} < CURRENT_TIMESTAMP + (INTERVAL '1 minute' * ${SESSION_FEEDBACK_NUM_MINUTES_EARLY})
         AND ${SessionQ.getComputedEndDate()} > CURRENT_TIMESTAMP - (INTERVAL '1 day' * ${SESSION_FEEDBACK_MAX_DAYS_BACK})
         ${SessionQ.where({
           seriesId: SeriesQ.id,
           order: [[SessionQ.start_date, 'desc']],
           limit: 1,
           offset: 1,
           // ^ 2nd last session in series
         })})`
    )
  )
  const seriesIdForTutorNPS = seriesForTutorNPS(SeriesQ.id)
  const seriesTitleForTutorNPS = seriesForTutorNPS(SeriesQ.title)

  const {
    acknowledgedAt: publishBootcampAcknowledgedAt,
    onAcknowledgement: onPublishBootcampAcknowledgement,
  } = useOneTimeAcknowledgement('sat-tutor-publish-bootcamp-after-level-up-08-16-23')

  const activeTutorBootcamp = query.one(
    SeriesQ.where(
      { limit: 1 },
      SQL`${SeriesQ.hasAnyTemplateSlug(SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS)}
        AND NOT ${SeriesQ.isClosed()}
        AND ${SeriesQ.isHost({ userId: user.id })}`
    )
  )
  const hasActiveBootcamp = activeTutorBootcamp(SeriesQ.id) != null

  const safetyReport = query.one(
    PlatformFeedbackQ.where(
      { userId: user.id, detectedIssueType: 'tutor-removed-learner', limit: 1 },
      PlatformFeedbackQ.hasStatus(SafetyReportStatus.AwaitingUserResponse)
    )
  )

  // Do not show feedback modal for suspended users
  if (user != null && (isUserSuspended(user) || user.roles.length === 0)) {
    return null
  }

  let content: JSX.Element | null = null

  if (renderContext.prepass || (!isUserOnboarding(user) && !dobIsSet)) {
    content = (
      <>
        {content}
        <MissingDateOfBirthModal />
      </>
    )

    if (!renderContext.prepass) {
      return content
    }
  }

  if (renderContext.prepass || userAdminMessageId != null) {
    content = (
      <>
        {content}
        <UserAdminMessageModal
          id={userAdminMessageId ?? 0}
          userId={userAdminMessageUserId}
          userAvatarUrl={userAdminMessageUserAvatarUrl}
          userDisplayName={userAdminMessageUserDisplayName}
          content={userAdminMessageContent}
          acknowledgmentText={userAdminMessageAcknowledgmentText}
          messageType={userAdminMessageType}
          isDisputable={isDisputable}
        />
      </>
    )

    if (!renderContext.prepass) {
      return content
    }
  }

  if (renderContext.prepass || safetyReport(PlatformFeedbackQ.id) != null) {
    content = (
      <>
        {content}
        <ReviewRemovedLearnerModal safetyReport={safetyReport} />
      </>
    )

    if (!renderContext.prepass) {
      return content
    }
  }

  if (
    !router.asPath.includes(`${SAT_BOOTCAMP_WORLD_SLUG}/schedule`) &&
    (renderContext.prepass ||
      (publishBootcampAcknowledgedAt == null &&
        !hasActiveBootcamp &&
        satBootcampTutorLeveledUpNotifyAt != null))
  ) {
    content = (
      <>
        {content}
        <PublishBootcampOnLevelUpModal onAcknowledgment={onPublishBootcampAcknowledgement} />
      </>
    )

    if (!renderContext.prepass) {
      return content
    }
  }

  if (renderContext.prepass || seriesIdForTutorNPS != null) {
    content = (
      <>
        {content}
        <SeriesFeedbackModalTutor
          seriesId={seriesIdForTutorNPS ?? 0}
          seriesTitle={seriesTitleForTutorNPS ?? ''}
        />
      </>
    )

    if (!renderContext.prepass) {
      return content
    }
  }

  if (renderContext.prepass || sessionId != null) {
    content = (
      <>
        {content}
        <ModalV3
          onClose={() => setOpen(false)}
          open={open}
          key={sessionId ?? 0}
          size="3xl"
          disableClickOutside
          disableClose
          title={() => (
            <Stack direction="row" align="center" spacing={2}>
              <Heading size="lg" as="h4">
                Share your feedback!
              </Heading>
              <Text color="fg.subtle" fontWeight={600}>
                {count > 1 ? ` ${count} remaining` : ''}
              </Text>
              <Spacer />
              <ShwLink
                href={`/report?${qs.stringify({
                  link: getSessionUrlById(sessionId ?? 0),
                })}`}
                isExternal
              >
                <Tippy content="Report a safety issue with this session.">
                  <IconButton
                    aria-label="Report a safety issue with this session"
                    isRound
                    variant="icon"
                    icon={<FontAwesomeIcon icon={faFlag} />}
                  />
                </Tippy>
              </ShwLink>
            </Stack>
          )}
          body={() => (
            <Box>
              <Box mb={6}>
                <SessionSummary
                  session={session}
                  hideHostAvatars={isDialoguesFeedback}
                  bg="bg.subtle"
                />
              </Box>
              <SessionFeedbackForm
                tutor={tutor}
                session={session}
                onSubmit={() => {
                  if (count === 1) {
                    setOpen(false)
                  }
                }}
              />
            </Box>
          )}
        />
      </>
    )
    if (!renderContext.prepass) {
      return content
    }
  }

  return content
}
